import { storage } from '@/utils/storage';
import { locationAPI } from '@/lib/api';
import { applySearchCountryFromLocation } from '@/lib/countryScope';

export type UserLocation = {
    city: string;
    state: string;
    country: string;
    countryCode: string;
    lat?: number;
    lon?: number;
    area?: string;
    pincode?: string;
    accuracy?: number;
    source?: string;
    catalog_enabled?: boolean;
    city_id?: number;
    state_id?: number;
    country_id?: number;
    locality_id?: number;
    disabled_reason?: string;
};

export type BrowserPosition = {
    lat: number;
    lon: number;
    accuracy?: number;
};

const GEO_OPTIONS_HIGH_ACCURACY: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
};

const GEO_OPTIONS_LOW_ACCURACY: PositionOptions = {
    enableHighAccuracy: false,
    timeout: 15000,
    maximumAge: 60000,
};

function sessionGeoKey(): string {
    let key = storage.session.getItem('geo_session_key');
    if (!key) {
        key = `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
        storage.session.setItem('geo_session_key', key);
    }
    return key;
}

function isUsLocation(location: Pick<UserLocation, 'countryCode' | 'country'>): boolean {
    const cc = (location.countryCode || '').toUpperCase();
    if (cc === 'US') return true;
    const country = (location.country || '').trim().toLowerCase();
    return (
        country === 'united states'
        || country === 'united states of america'
        || country === 'usa'
        || country === 'us'
    );
}

type ApiGeocodedLocation = NonNullable<
    Awaited<ReturnType<typeof locationAPI.recordBrowserPosition>>
>;

function hasGeocodedCountry(
    data: ApiGeocodedLocation | null | undefined,
): data is ApiGeocodedLocation {
    return Boolean(data?.countryCode?.trim() && data?.country?.trim());
}

function mapApiLocation(data: ApiGeocodedLocation): UserLocation {
    const countryCode = data.countryCode;
    const stripArea = (countryCode || '').toUpperCase() === 'US';
    return {
        city: (data.city || data.area || '').trim(),
        state: (data.state || '').trim(),
        country: data.country,
        countryCode: data.countryCode,
        lat: data.lat,
        lon: data.lon,
        area: stripArea ? undefined : data.area,
        pincode: data.pincode,
        source: data.source,
        catalog_enabled: data.catalog_enabled,
        city_id: data.city_id,
        state_id: data.state_id,
        country_id: data.country_id,
        locality_id: data.locality_id,
        disabled_reason: data.disabled_reason,
    };
}

/** Request browser GPS coordinates with High Accuracy GPS as primary, and standard accuracy as fallback on timeout/unavailable. */
export function getBrowserPosition(): Promise<BrowserPosition> {
    return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
            reject(new Error('Geolocation is not supported by this browser'));
            return;
        }

        const tryLowAccuracyFallback = () => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    storage.local.setItem('user_lat', String(position.coords.latitude));
                    storage.local.setItem('user_lng', String(position.coords.longitude));
                    storage.local.setItem('user_location_source', 'gps');
                    storage.local.setItem('gps_allowed', 'true');
                    resolve({
                        lat: position.coords.latitude,
                        lon: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                    });
                },
                (fallbackError) => {
                    if (fallbackError.code === fallbackError.PERMISSION_DENIED) {
                        storage.local.setItem('gps_allowed', 'false');
                    }
                    reject(fallbackError);
                },
                { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
            );
        };

        // Primary: Attempt high-accuracy GPS first
        navigator.geolocation.getCurrentPosition(
            (position) => {
                storage.local.setItem('user_lat', String(position.coords.latitude));
                storage.local.setItem('user_lng', String(position.coords.longitude));
                storage.local.setItem('user_location_source', 'gps');
                storage.local.setItem('gps_allowed', 'true');
                resolve({
                    lat: position.coords.latitude,
                    lon: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    storage.local.setItem('gps_allowed', 'false');
                    reject(error);
                } else {
                    // Timeout (code 3) or Position Unavailable (code 2) on high accuracy: try standard accuracy fallback
                    tryLowAccuracyFallback();
                }
            },
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 },
        );
    });
}

/** Reverse-geocode coordinates via backend location service and validate enabled city/state/country. */
export async function resolveLocationFromCoordinates(
    lat: number,
    lon: number,
    accuracy?: number,
): Promise<UserLocation | null> {
    try {
        const backendResult = await locationAPI.recordBrowserPosition(
            lat,
            lon,
            accuracy,
            sessionGeoKey(),
        );
        if (hasGeocodedCountry(backendResult)) {
            return mapApiLocation(backendResult);
        }
    } catch {
        console.warn('[location] Backend browser-position failed; trying GET reverse-geocode');
    }

    try {
        const fallback = await locationAPI.reverseGeocode(lat, lon, sessionGeoKey());
        if (hasGeocodedCountry(fallback)) {
            return mapApiLocation(fallback);
        }
    } catch {
        console.warn('[location] Backend reverse geocode failed');
    }

    return null;
}

export function clearCityFromStorage(): void {
    storage.local.removeItem('user_location');
    storage.local.removeItem('user_state');
    storage.local.removeItem('user_location_area');
    storage.local.removeItem('user_pincode');
    storage.local.removeItem('user_location_source');
    storage.local.removeItem('user_location_catalog_enabled');
    storage.local.removeItem('user_city_id');
    storage.local.removeItem('user_state_id');
    storage.local.removeItem('user_country_id');
    storage.local.removeItem('user_locality_id');
}

/** Persist resolved location (city + pincode + GPS) for search and listing flows. */
export function saveUserLocation(location: UserLocation): void {
    storage.local.setItem('user_location', location.city);
    storage.local.setItem('user_country', location.country);
    storage.local.setItem('user_country_code', location.countryCode);
    if (location.state) {
        storage.local.setItem('user_state', location.state);
    }
    if (location.area) {
        storage.local.setItem('user_location_area', location.area);
    } else {
        storage.local.removeItem('user_location_area');
    }
    if (location.pincode) {
        storage.local.setItem('user_pincode', location.pincode);
    } else {
        storage.local.removeItem('user_pincode');
    }
    if (location.lat != null && location.lon != null) {
        storage.local.setItem('user_lat', String(location.lat));
        storage.local.setItem('user_lng', String(location.lon));
    }
    if (location.accuracy != null) {
        storage.local.setItem('user_geo_accuracy', String(location.accuracy));
    }
    if (location.source) {
        storage.local.setItem('user_location_source', location.source);
    } else {
        storage.local.removeItem('user_location_source');
    }
    storage.local.setItem(
        'user_location_catalog_enabled',
        location.catalog_enabled ? 'true' : 'false',
    );
    if (location.city_id != null) {
        storage.local.setItem('user_city_id', String(location.city_id));
    } else {
        storage.local.removeItem('user_city_id');
    }
    if (location.state_id != null) {
        storage.local.setItem('user_state_id', String(location.state_id));
    } else {
        storage.local.removeItem('user_state_id');
    }
    if (location.country_id != null) {
        storage.local.setItem('user_country_id', String(location.country_id));
    } else {
        storage.local.removeItem('user_country_id');
    }
    if (location.locality_id != null) {
        storage.local.setItem('user_locality_id', String(location.locality_id));
    } else {
        storage.local.removeItem('user_locality_id');
    }
    if (location.source === 'gps') {
        storage.local.setItem('gps_allowed', 'true');
    }
    applySearchCountryFromLocation(location);
}

export function getStoredUserLocation(): Partial<UserLocation> | null {
    const city = storage.local.getItem('user_location');
    if (!city) return null;
    const latRaw = storage.local.getItem('user_lat');
    const lonRaw = storage.local.getItem('user_lng');
    const cityIdRaw = storage.local.getItem('user_city_id');
    const stateIdRaw = storage.local.getItem('user_state_id');
    const countryIdRaw = storage.local.getItem('user_country_id');
    const localityIdRaw = storage.local.getItem('user_locality_id');
    return {
        city,
        state: storage.local.getItem('user_state') || '',
        country: storage.local.getItem('user_country') || '',
        countryCode: storage.local.getItem('user_country_code') || '',
        area: storage.local.getItem('user_location_area') || undefined,
        pincode: storage.local.getItem('user_pincode') || undefined,
        lat: latRaw ? Number(latRaw) : undefined,
        lon: lonRaw ? Number(lonRaw) : undefined,
        accuracy: Number(storage.local.getItem('user_geo_accuracy') || '') || undefined,
        source: storage.local.getItem('user_location_source') || undefined,
        catalog_enabled: storage.local.getItem('user_location_catalog_enabled') === 'true',
        city_id: cityIdRaw ? Number(cityIdRaw) : undefined,
        state_id: stateIdRaw ? Number(stateIdRaw) : undefined,
        country_id: countryIdRaw ? Number(countryIdRaw) : undefined,
        locality_id: localityIdRaw ? Number(localityIdRaw) : undefined,
    };
}

/** Payload for property search APIs after validated GPS detection. */
export function getUserLocationContext(): {
    user_city?: string;
    user_state?: string;
    user_country?: string;
    user_pincode?: string;
    user_area?: string;
    user_city_id?: number;
} {
    const loc = getStoredUserLocation();
    if (!loc?.city || loc.catalog_enabled === false) {
        return {};
    }
    return {
        user_city: loc.city,
        user_state: loc.state || undefined,
        user_country: loc.country || undefined,
        user_pincode: loc.pincode || undefined,
        user_area: isUsLocation(loc as UserLocation) ? undefined : loc.area || undefined,
        user_city_id: loc.city_id,
    };
}

/** Human-readable label for GenUI / chat (pincode, area, city, state). */
export function formatLocationLabel(location: UserLocation): string {
    const includeArea = !isUsLocation(location);
    const parts = [
        location.pincode,
        includeArea ? location.area : null,
        location.city,
        location.state,
    ].filter((p) => typeof p === 'string' && p.trim());
    return parts.join(', ') || location.city;
}

/**
 * Full flow: browser permission → GPS → backend catalog validation → local storage.
 */
export async function detectUserLocation(): Promise<UserLocation | null> {
    console.info('[location] Starting location detection');
    try {
        const coords = await getBrowserPosition();
        const resolved = await resolveLocationFromCoordinates(
            coords.lat,
            coords.lon,
            coords.accuracy,
        );
        if (!resolved) {
            console.warn('[location] Could not reverse-geocode browser coordinates');
            return null;
        }
        if (resolved.countryCode && resolved.country) {
            applySearchCountryFromLocation(resolved);
        }
        if (resolved.catalog_enabled === false) {
            console.warn('[location] Detected location not in enabled catalog', resolved.disabled_reason);
            return resolved;
        }
        saveUserLocation({ ...resolved, source: 'gps', lat: coords.lat, lon: coords.lon });
        console.info('[location] Detection complete via browser GPS', resolved);
        return resolved;
    } catch (error) {
        console.warn('[location] Browser geolocation unavailable; using IP fallback', error);
    }

    try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.city && data.country_code && data.country_name) {
            const ipLocation: UserLocation = {
                city: data.city,
                state: data.region || '',
                country: data.country_name,
                countryCode: data.country_code,
                lat: typeof data.latitude === 'number' ? data.latitude : undefined,
                lon: typeof data.longitude === 'number' ? data.longitude : undefined,
                source: 'ip',
                catalog_enabled: false,
            };
            console.info('[location] Resolved via IP fallback (not catalog-validated)', ipLocation);
            return ipLocation;
        }
    } catch (error) {
        console.error('[location] IP detection fallback failed:', error);
    }

    return null;
}

/** Request browser GPS for property search when the user asks for "my location" (#119). */
export async function detectLocationForSearch(): Promise<
    { lat: number; lng: number } | { error: string }
> {
    try {
        const coords = await getBrowserPosition();
        const resolved = await resolveLocationFromCoordinates(
            coords.lat,
            coords.lon,
            coords.accuracy,
        );
        if (resolved && resolved.catalog_enabled !== false) {
            saveUserLocation({ ...resolved, source: 'gps', lat: coords.lat, lon: coords.lon });
        } else {
            storage.local.setItem('user_lat', String(coords.lat));
            storage.local.setItem('user_lng', String(coords.lon));
            storage.local.setItem('user_location_source', 'gps');
            storage.local.setItem('gps_allowed', 'true');
            if (resolved) {
                saveUserLocation({ ...resolved, source: 'gps', lat: coords.lat, lon: coords.lon });
            }
        }
        return { lat: coords.lat, lng: coords.lon };
    } catch {
        return { error: 'Location access was denied or unavailable.' };
    }
}

/** Use current GPS for a GenUI location widget reply. */
export async function detectLocationForListing(): Promise<
    { label: string; location: UserLocation } | { error: string }
> {
    try {
        const coords = await getBrowserPosition();
        const resolved = await resolveLocationFromCoordinates(
            coords.lat,
            coords.lon,
            coords.accuracy,
        );
        if (!resolved) {
            return { error: 'Could not resolve your coordinates to a place name.' };
        }
        if (resolved.catalog_enabled === false) {
            return {
                error:
                    resolved.disabled_reason ||
                    'Your current location is not enabled for listings in our catalog.',
            };
        }
        const gpsLocation: UserLocation = {
            ...resolved,
            source: 'gps',
            lat: coords.lat,
            lon: coords.lon,
        };
        saveUserLocation(gpsLocation);
        return { label: formatLocationLabel(gpsLocation), location: gpsLocation };
    } catch {
        return { error: 'Location access was denied or unavailable.' };
    }
}
